import {
  useCardProductInfos as _useCardProductInfos,
  createUseCanReviewProduct,
  createUseGiftItemBanner,
  createUseGiftItem,
  createUseResetPassword,
  createUsePaymentPreferences,
  createUseEnrichAlgoliaContexts,
  createUseHandleNotifications,
  createUseFrequentlyBoughtProducts,
  createUseMerchRecommendationsContents,
  useCreateCheckoutFromShoppingCart,
  createUseTagContextMatches,
  createUseHasReachedFreeDelivery,
} from '@monofront/business/hooks';
import { useAuthentication } from '@context/businessContainer';
import {
  productService,
  userService,
  prismicService,
} from '@services/businessContainer';
import { useShoppingCart } from './cart/useShoppingCart';
import { useGiftItemNotifications } from '@utils/giftItem';
import { GiftItemContext } from '@context/giftItem';
import { createOrGetPreferences } from '@services/userPreferences';
import createUseProductInfosInCheckout from '@monofront/business/hooks/checkout/createUseProductInfosInCheckout';
import { notificationToast } from '@utils/notificationToast';
import { useCacheFirstAuthentication } from '@context/persistedAuthContext';
import { getCurrentProductQuantity } from '@monofront/business/utils';
import getConfig from 'next/config';
import { createUseGTMConfig } from '@monofront/business/hooks/createUseGTMConfig';

const {
  publicRuntimeConfig: {
    gtmId = '',
    gtmUrl = '',
    gtmAuth = '',
    gtmPreview = '',
  },
} = getConfig();

const gtmDefaultConfig = {
  id: gtmId,
  url: gtmUrl,
  auth: gtmAuth,
  preview: gtmPreview,
};

export const useCardProductInfos = (
  product: Parameters<typeof _useCardProductInfos>[0],
) => {
  const { shoppingCart } = useShoppingCart();

  const quantity =
    'objectID' in product
      ? getCurrentProductQuantity(product, shoppingCart.products)
      : 1;

  return _useCardProductInfos(product, quantity);
};

export const useCanReviewProduct = createUseCanReviewProduct(
  useAuthentication,
  userService,
);

export const useFrequentlyBoughtProducts = createUseFrequentlyBoughtProducts(
  useAuthentication,
  userService,
);

export const useEnrichAlgoliaContexts = createUseEnrichAlgoliaContexts(
  useCacheFirstAuthentication,
);

export const useGiftItemBanner = createUseGiftItemBanner(
  useAuthentication,
  prismicService,
);
export const useMerchRecommendationsContents =
  createUseMerchRecommendationsContents(useAuthentication);

export const useGiftItem = createUseGiftItem(
  useAuthentication,
  useShoppingCart,
  () => useShoppingCart()?.shoppingCart,
  GiftItemContext,
  useGiftItemNotifications,
);

export const useResetPassword = createUseResetPassword(userService);

export const usePaymentPreferences = createUsePaymentPreferences(
  useAuthentication,
  createOrGetPreferences,
);
export const useSynchronizeCarts = useCreateCheckoutFromShoppingCart.bind(
  null,
  useAuthentication,
);

export const useProductInfosInCheckout =
  createUseProductInfosInCheckout(productService);

export const useHandleNotifications = createUseHandleNotifications(
  notificationToast,
  productService,
);

export const useGTMConfig = createUseGTMConfig(gtmDefaultConfig);

export const { useTagContext, useTagContextMatches } =
  createUseTagContextMatches(useAuthentication, false);

export const useHasReachedFreeDelivery =
  createUseHasReachedFreeDelivery(useShoppingCart);
